import type { AuthenticationResult, SilentRequest } from "@azure/msal-browser";
import { useNotificationsStore } from "@/store/notificationsStore";
import type { MsalFlowDeps, MsalFlowState } from "@ics/auth-api";

// *************************************************
// AXIOS OG TOKEN
//
// Token-cache og dedupe av parallelle requests lever i `deps.flowState` og
// eies dermed av MSAL-instans-eieren (provideren, task 2676) — to providere
// deler aldri cache eller kortslutter hverandres inflight-kall.

const tokenBuffer = 5 * 60 * 1000; // 5 minutter buffer

function isTokenValid (state: MsalFlowState, token: AuthenticationResult | null): boolean {
  return token !== null && Date.now() < (state.tokenExpiresAt - tokenBuffer);
}

export async function getTokenPopup (deps: MsalFlowDeps): Promise<AuthenticationResult | null> {
  const state = deps.flowState;
  if (isTokenValid(state, state.cachedToken)) {
    return state.cachedToken;
  }

  // Eksisterende logikk for å håndtere parallelle requests
  if (state.tokenRequestInProgress && state.lastTokenResult) {
    const token = await state.lastTokenResult;
    if (isTokenValid(state, token)) {
      console.log('Using cached token, expires at:', state.tokenExpiresAt);
      return token;
    }
  }

  state.tokenRequestInProgress = true;
  state.lastTokenResult = requestToken(deps);

  try {
    const token = await state.lastTokenResult;
    // Sett utløpstidspunkt når vi får nytt token
    if (token?.expiresOn) {
      // expiresOn skal være et Date-objekt ifølge MSAL-dokumentasjonen
      let expiresAt: number;
      if (token.expiresOn instanceof Date) {
        expiresAt = token.expiresOn.getTime();
      } else {
        // fallback hvis det mot formodning er en string
        expiresAt = new Date(token.expiresOn).getTime();
      }
      state.tokenExpiresAt = expiresAt;
      state.cachedToken = token;
      console.log('Set cached token - Token expires at:', state.tokenExpiresAt, new Date(state.tokenExpiresAt));
    }
    return token;
  } finally {
    state.tokenRequestInProgress = false;
    console.log('Token request completed');
  }
}

async function requestToken (deps: MsalFlowDeps): Promise<AuthenticationResult | null> {
  try {
    const instance = await deps.getInstance();
    const account = instance.getActiveAccount();
    const tokenConfig = await deps.getTokenRequest();

    // Kjør alltid force refresh for å sikre oppdaterte tokens
    if (account) {
      try {
        await instance.acquireTokenSilent({
          account: account,
          ...tokenConfig,
          forceRefresh: true
        });
      } catch (error) {
        await instance.clearCache();
      }
    }

    const tokenRequest: SilentRequest = {
      account: account || undefined,
      ...tokenConfig,
      prompt: "none"
    };

    // Prøv først silent token acquisition
    try {
      if (account) {
        const silentResult = await instance.acquireTokenSilent(tokenRequest);
        if (silentResult?.accessToken) {
          return silentResult;
        }
      }
    } catch (silentError: any) {
      // Spesifikk håndtering av scope-feil
      if (silentError.message?.includes('AADSTS650053') || silentError.message?.includes('invalid_client')) {
        console.error('Ugyldig scope-konfigurasjon:', silentError);
        const notificationsStore = useNotificationsStore();
        notificationsStore.addError(
          "Autentiseringsfeil",
          "Det oppstod en feil med tilgangsrettigheter. Kontakt systemadministrator."
        );
        // Ikke prøv popup hvis vi har scope-feil
        return null;
      }
      console.debug("[Auth] Silent token acquisition failed, trying popup");
    }

    // Popup med retry-logikk hvis silent feilet
    let retryCount = 0;
    const maxRetries = 2;

    while (retryCount <= maxRetries) {
      try {
        const popupResult = await instance.acquireTokenPopup({
          ...tokenRequest,
          prompt: "select_account"
        });

        if (popupResult?.accessToken) {
          return popupResult;
        }
        break;
      } catch (error: any) {
        if (error.name === "BrowserAuthError" && error.message.includes("popup_window_error")) {
          console.debug(`[Auth] Popup window error`);
          return null;
        }
        if (error.name === "BrowserAuthError" && error.message.includes("login_required")) {
          console.debug(`[Auth] Interaction in progress`);
          return null;
        }
        if (error.name === "BrowserAuthError" && error.message.includes("interaction_in_progress")) {
          console.debug(`[Auth] Interaction in progress`);
          return null;
        }
        if (error.name === "BrowserAuthError" && error.message.includes("hash_empty_error")) {
          if (retryCount < maxRetries) {
            console.debug(`[Auth] Hash empty error, retry ${retryCount + 1} of ${maxRetries}`);
            await new Promise(resolve => setTimeout(resolve, 1000));
            retryCount++;
            continue;
          }
        }
        throw error;
      }
    }

    throw new Error("Kunne ikke hente token");
  } catch (error: any) {
    // Spesifikk håndtering av scope-feil på toppnivå
    if (error.message?.includes('AADSTS650053') || error.message?.includes('invalid_client')) {
      console.error('Ugyldig scope-konfigurasjon:', error);
      const notificationsStore = useNotificationsStore();
      notificationsStore.addError(
        "Autentiseringsfeil",
        "Det oppstod en feil med tilgangsrettigheter. Kontakt systemadministrator."
      );
      return null;
    }
    console.error("Token acquisition failed:", error);
    throw error;
  }
}

// Hent token kun via silent refresh (for SignalR - unngår popup)
export async function getTokenSilent (deps: MsalFlowDeps): Promise<AuthenticationResult | null> {
  const state = deps.flowState;
  // Sjekk om cached token er gyldig
  if (isTokenValid(state, state.cachedToken)) {
    return state.cachedToken;
  }

  try {
    const instance = await deps.getInstance();
    const account = instance.getActiveAccount();
    const tokenConfig = await deps.getTokenRequest();

    if (!account) {
      console.warn('[Auth] Ingen account tilgjengelig for silent token refresh');
      return null;
    }

    const tokenRequest: SilentRequest = {
      account: account,
      ...tokenConfig,
      prompt: "none"
    };

    // Prøv kun silent token acquisition (ingen popup)
    const silentResult = await instance.acquireTokenSilent(tokenRequest);

    if (silentResult?.accessToken) {
      // Oppdater cache
      if (silentResult.expiresOn) {
        let expiresAt: number;
        if (silentResult.expiresOn instanceof Date) {
          expiresAt = silentResult.expiresOn.getTime();
        } else {
          expiresAt = new Date(silentResult.expiresOn).getTime();
        }
        state.tokenExpiresAt = expiresAt;
        state.cachedToken = silentResult;
      }
      return silentResult;
    }

    return null;
  } catch (error: any) {
    // Silent refresh feilet - logg men ikke vis popup
    console.debug('[Auth] Silent token refresh failed for SignalR:', error);
    return null;
  }
}
